// src/components/SharePointSearch/SharePointError.tsx

import * as React from "react";
import { NormalizedSharePointError } from "../../interfaces/sharepointSearchTypes";
import { LockIcon, ClockIcon, InfoCircleIcon } from "./SharePointIcons";
import { spColors } from "./sharepointSearchStyles";

interface SharePointErrorProps {
  result: NormalizedSharePointError;
}

/** Error code categories for contextual icon selection */
const PERMISSION_ERRORS = [
  "USER_ERROR_PERMISSION_DENIED",
  "OBO_TOKEN_ERROR",
  "AUTHENTICATION_ERROR",
];

const TIMEOUT_ERRORS = ["CONNECTION_TIMEOUT", "REQUEST_TIMEOUT", "USER_ERROR_TIMEOUT"];

/**
 * Contextual error display for SharePoint tool failures.
 * Shows different icons and tips based on error type (permission, timeout, file errors).
 */
const SharePointError: React.FC<SharePointErrorProps> = ({ result }) => {
  const isPermissionError = PERMISSION_ERRORS.some((code) => result.errorCode.includes(code));
  const isTimeoutError = TIMEOUT_ERRORS.some((code) => result.errorCode.includes(code));

  const getIcon = (): React.ReactNode => {
    if (isPermissionError) return <LockIcon />;
    if (isTimeoutError) return <ClockIcon />;
    return <InfoCircleIcon />;
  };

  const getHeader = (): string => {
    if (isPermissionError) return "Access denied";
    if (isTimeoutError) return "Connection timed out";
    return "Search failed";
  };

  const getTips = (): string[] => {
    if (isPermissionError) {
      return ["Please sign out and sign back in", "Verify you have access to the SharePoint site"];
    }
    if (isTimeoutError) {
      return ["Try again in a moment", "Use more specific search terms"];
    }
    return [];
  };

  const bgColor = isTimeoutError ? spColors.warningBg : spColors.errorBg;
  const textColor = isTimeoutError ? spColors.warningColor : spColors.errorColor;
  const tips = getTips();

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "8px",
          padding: "10px 12px",
          borderRadius: "6px",
          backgroundColor: bgColor,
          fontSize: "12px",
          lineHeight: "1.5",
          color: textColor,
        }}
      >
        {getIcon()}
        <div>
          <p style={{ margin: 0, fontWeight: 500 }}>{getHeader()}</p>
          <p style={{ margin: "4px 0 0", opacity: 0.8 }}>{result.errorMessage}</p>
        </div>
      </div>

      {tips.length > 0 && (
        <ul
          style={{
            margin: 0,
            paddingLeft: "20px",
            fontSize: "11px",
            color: spColors.mutedForeground,
            lineHeight: "1.6",
          }}
        >
          {tips.map((tip, idx) => (
            <li key={idx}>{tip}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SharePointError;
